import { taskLabel } from "@/lib/task-reference";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  type CarrierTask,
  useAssignCarrierTaskOperator,
  useCarrierOperators,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog, type DialogProps } from "@/components/ui/dialog";
import { NativeSelect } from "@/components/ui/native-select";
import { hasHttpStatus } from "@/lib/api-error";
import { operatorDisplayName } from "@/lib/operator-identity";

interface AssignTaskOperatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: Pick<
    CarrierTask,
    "id" | "taskNumber" | "operatorId" | "dispatchCenterId"
  > &
    Partial<Pick<CarrierTask, "shipment">>;
  dialogPresentation?: DialogProps["presentation"];
}

export function AssignTaskOperatorDialog({
  open,
  onOpenChange,
  task,
  dialogPresentation,
}: AssignTaskOperatorDialogProps) {
  const operators = useCarrierOperators({ limit: 100 });
  const assign = useAssignCarrierTaskOperator();
  const initialOperatorId = task.operatorId ?? "";
  const [operatorId, setOperatorId] = useState(initialOperatorId);
  const submitLock = useRef(false);
  const responsive = dialogPresentation === "responsive-sheet";
  const availableOperators = useMemo(
    () =>
      (operators.data?.data ?? []).filter(
        (operator) =>
          task.dispatchCenterId !== null &&
          operator.dispatchCenterId === task.dispatchCenterId
      ),
    [operators.data?.data, task.dispatchCenterId]
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (operatorId === initialOperatorId || submitLock.current) return;
    submitLock.current = true;
    try {
      await assign.mutateAsync({
        taskId: task.id,
        operatorId: operatorId || null,
      });
      toast.success(
        operatorId ? "Task operator reassigned." : "Task operator cleared."
      );
      onOpenChange(false);
    } catch {
      toast.error("Failed to update the Task operator.");
    } finally {
      submitLock.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!assign.isPending) onOpenChange(nextOpen);
      }}
      title={`Assign operator · ${taskLabel(task)}`}
      presentation={dialogPresentation}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="task-operator-picker"
            className="mb-1 block text-sm font-medium"
          >
            Operator
          </label>
          <NativeSelect
            id="task-operator-picker"
            value={operatorId}
            onChange={(event) => setOperatorId(event.target.value)}
            disabled={operators.isLoading || assign.isPending}
          >
            <option value="">Unassigned</option>
            {availableOperators.map((operator) => {
              const name = operatorDisplayName(operator);
              return (
                <option key={operator.id} value={operator.id}>
                  {name}
                </option>
              );
            })}
          </NativeSelect>
        </div>

        {task.dispatchCenterId === null && (
          <p role="alert" className="text-sm text-destructive">
            Assign this Task to a Dispatch Center before choosing an Operator.
          </p>
        )}
        {operators.error && (
          <p role="alert" className="text-sm text-destructive">
            {hasHttpStatus(operators.error, 403)
              ? "You do not have permission to load Operators."
              : "Operators could not be loaded. Try again."}
          </p>
        )}
        {!operators.isLoading &&
          !operators.error &&
          task.dispatchCenterId !== null &&
          availableOperators.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No Operators are available in this Dispatch Center.
            </p>
          )}
        {assign.isError && (
          <p role="alert" className="text-sm text-destructive">
            {hasHttpStatus(assign.error, 409)
              ? "This Task changed or now belongs to a Trip. Refresh and try again."
              : "The Task operator could not be updated. Try again."}
          </p>
        )}

        <div
          className={
            responsive
              ? "sticky bottom-0 grid grid-cols-2 gap-2 border-t bg-background pt-4"
              : "flex justify-end gap-2"
          }
        >
          <Button
            type="button"
            variant="outline"
            size={responsive ? "touch" : "default"}
            onClick={() => onOpenChange(false)}
            disabled={assign.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size={responsive ? "touch" : "default"}
            disabled={
              operatorId === initialOperatorId ||
              assign.isPending ||
              operators.isLoading ||
              !!operators.error ||
              task.dispatchCenterId === null
            }
          >
            {assign.isPending ? "Saving…" : "Save assignment"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
